import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Stack, Typography } from '@mui/material';

import { AppTab, AppTabs } from 'src/components/app-tabs';
import { AppDialog } from 'src/components/app-dialog';
import { AppButton, StackColumn, StackRow } from 'src/components';
import { IconArrowRight, IconDot } from 'src/libraries/icons';
import { PATH_DASHBOARD } from 'src/routes/paths';

//
//

const DIALOG_CLOSE_DURATION_MS = 300;

//
//

const Page1 = () => (
  <StackColumn>
    <Typography>
      Welcome to CarbonClick. You've signed up for Event Offsetting — a complete toolkit for
      measuring, offsetting, and communicating the carbon impact of your events.
    </Typography>
    <Typography color="text.secondary">Here's how it works.</Typography>
  </StackColumn>
);

const Page2 = () => {
  const bullets = [
    'A recurring conference you host at a specific venue',
    'All events held at a particular property or location',
    'Events you manage on behalf of a specific client',
    'A dedicated initiative that needs its own impact report',
  ];

  return (
    <StackColumn>
      <Typography>
        A Programme is a specific sustainability initiative — with its own emissions, offsets, and
        landing page that you share with clients and stakeholders.
      </Typography>
      <Typography color="text.secondary">
        Think of it as the place where a sustainability story begins and ends. The landing page is
        what your stakeholders see.
      </Typography>
      <Typography color="text.secondary" variant="overline">
        You might create a Programme for
      </Typography>

      <StackColumn gap={1}>
        {bullets.map((text) => (
          <Stack key={text} flexWrap="wrap" flexDirection="row" alignItems="center">
            <IconDot
              size={16}
              style={{ transform: 'scale(3)', transformOrigin: 'center', marginRight: 8 }}
            />
            <Typography>{text}</Typography>
          </Stack>
        ))}

        <Typography>
          You can create as many Programmes as you need, and switch between them at any time.
        </Typography>
      </StackColumn>
    </StackColumn>
  );
};

enum TABS {
  CALCULATE = 0,
  OFFSET = 1,
  COMMUNICATE = 2,
}

type Page3Props = { tab: number; onTabChange: (tab: number) => void };

const Page3 = ({ tab, onTabChange }: Page3Props) => (
  <StackColumn>
    <Typography color="text.secondary">Click through each step to see how it works.</Typography>
    <AppTabs value={tab} onChange={(_, v) => onTabChange(v)}>
      <AppTab label="Calculate" value={TABS.CALCULATE} />
      <AppTab label="Offset" value={TABS.OFFSET} />
      <AppTab label="Communicate" value={TABS.COMMUNICATE} />
    </AppTabs>
    {tab === TABS.CALCULATE && (
      <StackColumn>
        <Typography>
          Use the event calculator to measure carbon emissions across every part of your event —
          energy use, delegate travel, staff travel, food, and freight.
        </Typography>
        <Typography color="text.secondary">
          If your Programme covers multiple events over time, you can add as many footprint
          calculations as you need. Each one is saved separately and rolls up into your Programme
          total.
        </Typography>
      </StackColumn>
    )}
    {tab === TABS.OFFSET && (
      <StackColumn>
        <Typography>
          Choose from a curated portfolio of independently verified carbon offsetting projects —
          from forest conservation to clean cookstoves to renewable energy.
        </Typography>
        <Typography color="text.secondary">
          Select the projects that align with your values, purchase your offset, and a certificate
          is generated automatically. You can configure a preferred project selection for your
          Programme so repeat offsets stay fast.
        </Typography>
      </StackColumn>
    )}
    {tab === TABS.COMMUNICATE && (
      <StackColumn>
        <Typography>
          Every Programme has its own landing page — a live page showing the total emissions
          you've offset, the projects you've supported, and your cumulative impact over time. It
          updates automatically as you add more offsets.
        </Typography>
        <Typography color="text.secondary">
          You can also add a travel calculator widget so delegates or guests can measure and
          offset their own travel directly from your page. Share it with clients, include it in
          proposals, or link from your website. Your sustainability story, always up to date.
        </Typography>
      </StackColumn>
    )}
  </StackColumn>
);

type Page4Props = { onClose: () => void };

const Page4 = ({ onClose }: Page4Props) => {
  const navigate = useNavigate();

  return (
    <StackColumn>
      <Typography>
        Start by creating your first Programme. Give it a name — you can always rename it later —
        and everything else follows from there.
      </Typography>

      <StackColumn gap={1} alignItems="center">
        <AppButton
          variant="contained"
          size="small"
          onClick={() => {
            navigate(PATH_DASHBOARD.programs.root);
            onClose();
          }}
        >
          Create your first Programme
        </AppButton>
        <AppButton variant="text" size="small" onClick={onClose}>
          I'll explore first
        </AppButton>
      </StackColumn>
    </StackColumn>
  );
};

//
//

type OnboardingDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const OnboardingDialog: React.FC<OnboardingDialogProps> = ({ open, onClose }) => {
  const [page, setPage] = useState(0);
  const [tab, setTab] = useState(TABS.CALCULATE);

  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setPage(0);
        setTab(TABS.CALCULATE);
      }, DIALOG_CLOSE_DURATION_MS);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const PAGES = useMemo(
    () => [
      { title: 'Turn your events into a sustainability story.', content: <Page1 /> },
      { title: 'Your work happens inside a Programme.', content: <Page2 /> },
      {
        title: 'Three steps, from footprint to story.',
        content: <Page3 tab={tab} onTabChange={setTab} />,
      },
      { title: "You're ready to go.", content: <Page4 onClose={onClose} /> },
    ],
    [tab, onClose]
  );

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={PAGES[page].title}
      sx={{ '& .MuiDialogContent-root .MuiTypography-root': { fontSize: 14 } }}
      actions={
        <StackRow justifyContent="space-between" width="100%">
          <Typography color="text.secondary" variant="body2">
            {page + 1} of {PAGES.length}
          </Typography>
          {page < PAGES.length - 1 ? (
            <AppButton
              variant="contained"
              size="small"
              sx={{ gap: 1 }}
              onClick={() => setPage((p) => p + 1)}
            >
              Next <IconArrowRight size={16} />
            </AppButton>
          ) : (
            <AppButton variant="contained" size="small" onClick={onClose}>
              Close
            </AppButton>
          )}
        </StackRow>
      }
    >
      {PAGES[page].content}
    </AppDialog>
  );
};
